import { at, num, f, record, list, values, entries, isArray } from './support-b1';
import { empty, isset, strval, identical, PhpFloat } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
interface Deps {
    ft_shape_with_roof_decks(shape: unknown, z: unknown): unknown;
}
/** PHP: ft3d_roof_ridge_kit (includes/functions/janus/encounter/ft3d_floor_band.php:29) */
export function ft3dRoofRidgeKit(id: string): unknown {
    switch (id.toUpperCase()) {
        case '305044BB-11B2-B565-ABCC-E1A04FBCC6CE':
        case '97C0FCB5-8943-71DC-E316-469B486702A5':
        case '3299FC30-23BA-680F-E47B-C556704F6C77': return record({ standFt: f(8.3), ridgeHalfFt: null });
        case '10F51ACE-1672-AFA1-1602-99D60DB4A46D': return record({ standFt: f(8.2), ridgeHalfFt: f(5) });
        case '8412BBD8-8768-84E3-0E58-3494C379FC58': return record({ standFt: f(6.9), ridgeHalfFt: f(5) });
        default: return null;
    }
}
const span = (a: number, b: number) => record({ min: f(a), max: f(b) });
/** PHP: ft_shape_roof_deck_boxes (includes/functions/janus/encounter/ft3d_floor_band.php:42) */
export function ftShapeRoofDeckBoxes(shape: unknown, maxLevel: unknown = null): unknown {
    maxLevel = maxLevel === null ? null : f(num(maxLevel));
    const bound = new Set<string>();
    for (const b of values(at(shape, 'authoring', 'buildingRoofs'))) {
        if (!isArray(b) || empty(at(b, 'inventoryId')))
            continue;
        for (const id of values(at(b, 'roomIds')))
            bound.add(strval(id));
    }
    const structural = values(at(shape, 'boxes')).filter(b => isArray(b) && empty(at(b, 'roofDeck')) && [at(b, 'x', 'min'), at(b, 'y', 'min'), at(b, 'floor'), at(b, 'ceiling')].every(v => isset(v)) && at(b, 'navigationOpenGround') !== true && at(b, 'creation', 'enclosure') !== 'none' && at(b, 'creation', 'environment') !== 'exterior-open' && at(b, 'creation', 'presetId') !== 'exterior-open');
    const decks: unknown[] = [], byId = new Map(structural.map(b => [strval(at(b, 'id') ?? ''), b]));
    for (const [key, b] of entries(at(shape, 'authoring', 'buildingRoofs'))) {
        if (!isArray(b) || empty(at(b, 'inventoryId')))
            continue;
        const members = values(at(b, 'roomIds')).map(id => byId.get(strval(id)) ?? null);
        if (!members.length || members.includes(null) || members.some(m => at(m, 'creation', 'roofRemoved') === true))
            continue;
        const kit = ft3dRoofRidgeKit(strval(at(b, 'meshId') ?? '')), eave = num(at(b, 'z') ?? NaN), cx = num(at(b, 'x') ?? NaN), cy = num(at(b, 'y') ?? NaN), depth = num(at(b, 'depthFt') ?? NaN);
        if (kit === null || !Number.isFinite(eave) || !Number.isFinite(cx) || !Number.isFinite(cy) || !(depth > 0))
            continue;
        const level = eave + num(at(kit, 'standFt')), half = at(kit, 'ridgeHalfFt') === null ? depth / 2 : Math.max(.5, num(at(kit, 'ridgeHalfFt')) * depth / 30);
        if (maxLevel !== null && level > num(maxLevel) + .05)
            continue;
        const width = num(at(b, 'widthFt') ?? 0);
        decks.push(record({ id: 'roof-ridge:' + strval(key), x: span(cx - 2.5, cx + 2.5), y: span(cy - half, cy + half), floor: f(level), ceiling: f(level + 1000), navigationOpenGround: true, roofDeck: true, ridge: true, hipped: at(kit, 'ridgeHalfFt') !== null, eaveFt: f(eave), roofOf: strval(key), roomIds: list(values(at(b, 'roomIds')).map(v => strval(v))), roofInventoryId: strval(at(b, 'inventoryId')).toUpperCase(), roofFootprint: record({ minX: f(cx - width / 2), maxX: f(cx + width / 2), minY: f(cy - depth / 2), maxY: f(cy + depth / 2) }) }));
    }
    for (const wall of values(at(shape, 'sceneWalls'))) {
        if (!isArray(wall) || num(at(wall, 'thicknessFt') ?? 0) < 2 || !['x', 'y'].includes(strval(at(wall, 'axis'))))
            continue;
        const level = num(at(wall, 'z', 'max') ?? NaN);
        if (!Number.isFinite(level) || maxLevel !== null && level > num(maxLevel) + .05)
            continue;
        const half = num(at(wall, 'thicknessFt')) / 2, pos = num(at(wall, 'at')), a = num(at(wall, 'span', 'min')), b = num(at(wall, 'span', 'max')), x = at(wall, 'axis') === 'x';
        decks.push(record({ id: 'wall-top:' + strval(at(wall, 'id') ?? ''), x: x ? span(pos - half, pos + half) : span(a, b), y: x ? span(a, b) : span(pos - half, pos + half), floor: f(level), ceiling: f(level + 1000), navigationOpenGround: true, roofDeck: true, wallTop: true, roofOf: strval(at(wall, 'id') ?? '') }));
    }
    for (const box of structural) {
        if (!strval(at(box, 'creation', 'enclosure') ?? '').includes('ceiling') || at(box, 'creation', 'roofRemoved') === true || bound.has(strval(at(box, 'id') ?? '')))
            continue;
        const level = num(at(box, 'creation', 'physicalCeilingFt') ?? at(box, 'ceiling'));
        if (!Number.isFinite(level) || maxLevel !== null && level > num(maxLevel) + .05)
            continue;
        const x0 = num(at(box, 'x', 'min')), x1 = num(at(box, 'x', 'max')), y0 = num(at(box, 'y', 'min')), y1 = num(at(box, 'y', 'max'));
        let radius = Math.max(0, num(at(box, 'creation', 'footprint', 'cornerRadiusFt') ?? 0), ...values(at(box, 'creation', 'footprint', 'cornerRadiiFt')).map(num));
        radius = Math.min(radius, (x1 - x0) / 2, (y1 - y0) / 2);
        const rects = radius > .01 ? [[x0, x1, y0 + radius, y1 - radius], [x0 + radius, x1 - radius, y0, y1]] : [[x0, x1, y0, y1]];
        let skip = false;
        for (const other of structural) {
            if (identical(other, box))
                continue;
            const ox0 = num(at(other, 'x', 'min')), ox1 = num(at(other, 'x', 'max')), oy0 = num(at(other, 'y', 'min')), oy1 = num(at(other, 'y', 'max'));
            if (Math.min(x1, ox1) - Math.max(x0, ox0) <= .01 || Math.min(y1, oy1) - Math.max(y0, oy0) <= .01)
                continue;
            const covers = ox0 <= x0 + .01 && ox1 >= x1 - .01 && oy0 <= y0 + .01 && oy1 >= y1 - .01;
            if (Math.abs(num(at(other, 'floor')) - level) <= .01 && covers || level > num(at(other, 'floor')) + .01 && level < num(at(other, 'ceiling')) - .01) {
                skip = true;
                break;
            }
        }
        if (skip)
            continue;
        for (const [i, r] of rects.entries()) {
            const [a, b, c, d] = r as [
                number,
                number,
                number,
                number
            ];
            if (b - a <= .01 || d - c <= .01)
                continue;
            decks.push(record({ id: 'roof-deck:' + strval(at(box, 'id') ?? '') + ':' + i, x: span(a, b), y: span(c, d), floor: f(level), ceiling: f(level + 1000), navigationOpenGround: true, roofDeck: true, roofOf: strval(at(box, 'id') ?? '') }));
        }
    }
    return list(decks);
}
/** PHP: ft_floor_band_room_inputs (includes/functions/janus/encounter/ft3d_floor_band.php:183) */
export function ftFloorBandRoomInputs(boxes: unknown): unknown {
    const inputs = [];
    for (const box of values(boxes)) {
        if (!isArray(box))
            continue;
        const raw = [at(box, 'x', 'min'), at(box, 'x', 'max'), at(box, 'y', 'min'), at(box, 'y', 'max'), at(box, 'floor'), at(box, 'ceiling')];
        if (raw.some(v => !(typeof v === 'number' || typeof v === 'bigint' || v instanceof PhpFloat) || !Number.isFinite(num(v))))
            continue;
        const [xmin, xmax, ymin, ymax, floor, ceiling] = raw.map(num) as [
            number,
            number,
            number,
            number,
            number,
            number
        ];
        if (xmax <= xmin || ymax <= ymin || ceiling <= floor)
            continue;
        inputs.push(list([box, ...[xmin, xmax, ymin, ymax, floor, ceiling, (xmax - xmin) * (ymax - ymin)].map(f)]));
    }
    return list(inputs);
}
/** PHP: ft_floor_band_room_from_inputs (includes/functions/janus/encounter/ft3d_floor_band.php:197) */
export function ftFloorBandRoomFromInputs(inputs: unknown, x: unknown, y: unknown, z: unknown): unknown {
    x = f(num(x));
    y = f(num(y));
    z = f(num(z));
    if (![x, y, z].every(v => Number.isFinite(num(v))))
        return null;
    let winner: unknown = null, area = Infinity, contact = false;
    for (const r of values(inputs)) {
        const box = at(r, 0), xmin = num(at(r, 1)), xmax = num(at(r, 2)), ymin = num(at(r, 3)), ymax = num(at(r, 4)), floor = num(at(r, 5)), ceiling = num(at(r, 6)), footprint = num(at(r, 7));
        if (num(x) < xmin || num(x) > xmax || num(y) < ymin || num(y) > ymax || num(z) < floor - .01)
            continue;
        const touches = Math.abs(num(z) - floor) <= .01;
        if (!touches && num(z) >= ceiling - .01)
            continue;
        if (touches !== contact ? touches : footprint < area) {
            winner = box;
            area = footprint;
            contact = touches;
        }
    }
    return winner;
}
/** PHP: ft_shape_floor_band_room_for_position (includes/functions/janus/encounter/ft3d_floor_band.php:175) */
export function ftShapeFloorBandRoomForPosition(shape: unknown, x: unknown, y: unknown, z: unknown, includeSupport = true, env: PortEnv<Deps>): unknown {
    x = f(num(x));
    y = f(num(y));
    z = f(num(z));
    if (![x, y, z].every(v => Number.isFinite(num(v))))
        return null;
    if (includeSupport)
        shape = env.deps.ft_shape_with_roof_decks(shape, z);
    return ftFloorBandRoomFromInputs(ftFloorBandRoomInputs(at(shape, 'boxes') ?? []), x, y, z);
}
